import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { updateBusinessSummary } from 'lib/api';
import { getBusinessUnits } from 'lib/api/business';
import { searchOrCreateBusiness } from 'lib/api/business';
import {
  searchOrCreateBusinessExpress,
  updateBusinessSummaryExpress,
} from 'lib/api/express';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatAddress, showAlert } from 'lib/functions/utils';
import { Business, BusinessSummary, BusinessUnit } from 'lib/types/models';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import AddressEditor from './AddressEditor';
import BusinessCard from './BusinessCard';

interface BusinessDetailsModalProps {
  open: boolean;
  onClose: () => void;
  identifier: string;
  onSelect?: (
    business: Business,
    summary?: BusinessSummary,
    unit?: BusinessUnit,
  ) => void;
  express?: boolean;
}

const BusinessDetailsModal: React.FC<BusinessDetailsModalProps> = ({
  open,
  onClose,
  identifier,
  onSelect,
  express = false,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState<Business | null>(null);
  const [summary, setSummary] = useState<BusinessSummary | null>(null);
  const [units, setUnits] = useState<BusinessUnit[]>([]);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    region: '',
    province: '',
    commune: '',
    address: '',
  });

  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];

  const fetchBusinessData = useCallback(async () => {
    if (!open || !identifier) return;
    setLoading(true);
    try {
      const businessRes = express
        ? await searchOrCreateBusinessExpress(dispatch, secretId, identifier)
        : await searchOrCreateBusiness(dispatch, identifier);

      if (!businessRes) {
        setBusiness(null);
        setSummary(null);
        return;
      }
      setBusiness(businessRes.business);
      setSummary(businessRes.summary);
      if (businessRes.summary) {
        setAddressForm({
          region: businessRes.summary.region || '',
          province: businessRes.summary.province || '',
          commune: businessRes.summary.commune || '',
          address: businessRes.summary.address || '',
        });
      }
    } catch (error) {
      console.error('Error al obtener datos de la empresa:', error);
      showAlert(dispatch, 'error', 'Error al obtener datos de la empresa');
      setBusiness(null);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [dispatch, identifier, open, express, secretId]);

  const fetchBusinessUnits = useCallback(async () => {
    if (!open || !business) return;
    setLoading(true);
    try {
      const unitsRes = await getBusinessUnits(dispatch, business.identifier);
      setUnits(unitsRes);
    } catch (error) {
      console.error('Error al obtener unidades de negocio:', error);
      showAlert(dispatch, 'error', 'Error al obtener unidades de negocio');
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, business, open]);

  const handleSaveAddress = async () => {
    if (!business) return;

    setLoading(true);
    try {
      if (express) {
        await updateBusinessSummaryExpress(
          dispatch,
          secretId,
          business.identifier,
          addressForm,
        );
      } else {
        await updateBusinessSummary(dispatch, business.identifier, addressForm);
      }
      setSummary((prev) => (prev ? { ...prev, ...addressForm } : null));
      setEditingAddress(false);
      showAlert(dispatch, 'success', 'Dirección actualizada correctamente');
    } catch (error) {
      console.error('Error al actualizar la dirección:', error);
      showAlert(dispatch, 'error', 'Error al actualizar la dirección');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPrincipal = () => {
    if (onSelect && business) {
      onSelect(business, summary ?? undefined, undefined);
      onClose();
    }
  };

  const handleSelectUnit = (unit: BusinessUnit) => {
    if (onSelect && business) {
      onSelect(business, summary ?? undefined, unit);
      onClose();
    }
  };

  const handleAddressChange = (
    field: keyof typeof addressForm,
    value: string,
  ) => {
    setAddressForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (open) void fetchBusinessData();
  }, [open, fetchBusinessData]);

  useEffect(() => {
    if (open && business) void fetchBusinessUnits();
  }, [open, business, fetchBusinessUnits]);

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <CenteredCircularProgress type="dialog" />
      </Dialog>
    );
  }

  if (!business) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Empresa no encontrada</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <AlertBanner
            severity="error"
            title="Empresa no encontrada"
            message={`No se encontró empresa con identificador: ${identifier}`}
            description="Verifica que el identificador sea correcto o contacta al administrador del sistema."
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Seleccionar Mandante
            </Typography>
            <Chip
              label="Direcciones"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Selecciona la empresa o una unidad de negocio:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BusinessCard business={business} onClick={handleSelectPrincipal}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LocationOnIcon
                    sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Dirección
                  </Typography>
                  {editingAddress ? (
                    <AddressEditor
                      addressForm={addressForm}
                      onAddressChange={handleAddressChange}
                      onSave={() => void handleSaveAddress()}
                      onCancel={() => setEditingAddress(false)}
                      loading={loading}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ fontSize: '0.75rem', flex: 1 }}
                      >
                        {formatAddress(summary)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingAddress(true);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </BusinessCard>
          </Grid>

          {units.map((unit) => (
            <Grid item xs={12} key={unit.unitCode}>
              <BusinessCard
                business={unit}
                isUnit={true}
                onClick={() => handleSelectUnit(unit)}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocationOnIcon
                      sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Dirección
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatAddress(unit.address)}
                    </Typography>
                  </Box>
                </Box>
              </BusinessCard>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessDetailsModal;
